import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { forumAPI } from "@/lib/api";
import { FORUM_CATEGORIES } from "@/pages/Forum";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ImagePlus, X } from "lucide-react";
import ProductSelector from "@/components/ProductSelector";

export default function CreateForumPost() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [tags, setTags] = useState("");
  const [images, setImages] = useState([]);
  const [gear, setGear] = useState({ manufacturer: "", product: "" });
  const [saving, setSaving] = useState(false);

  const pickImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - images.length;
    files.slice(0, remaining).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setImages((prev) => [...prev, ev.target.result].slice(0, 5));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    const equipment_tag =
      gear.product && gear.product !== "General"
        ? `${gear.manufacturer} ${gear.product}`.trim()
        : "any";
    try {
      const res = await forumAPI.createPost({
        title,
        content,
        category,
        equipment_tag,
        images,
        tags: tags ? tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      });
      const newId = res.data?.id || res.data?.post?.id;
      if (res.data?.moderation_status === "pending") {
        toast.success("Your post is pending review before it goes live. Sorry for any wait; we review quickly.");
        navigate("/forum");
        return;
      }
      toast.success("Post published");
      navigate(newId ? `/forum/${newId}` : "/forum");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <button onClick={() => navigate("/forum")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Forum
      </button>
      <h1 className="font-display text-2xl font-bold">New discussion</h1>
      <Card className="border-border bg-card p-5">
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What's on your mind?" data-testid="forum-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="forum-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORUM_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {category !== "general" && (
            <div className="space-y-2">
              <Label>Related product (optional)</Label>
              <ProductSelector
                manufacturer={gear.manufacturer}
                product={gear.product}
                onChange={setGear}
                allowGeneral
                testIdPrefix="forum-product"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea id="content" rows={8} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share the details..." data-testid="forum-content-input" />
          </div>

          <div className="space-y-2">
            <Label>Images (optional, up to 5)</Label>
            {images.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {images.map((img, i) => (
                  <div key={i} className="relative h-20 w-20 overflow-hidden rounded-md border border-border">
                    <img src={img} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-background/80 text-foreground hover:bg-primary hover:text-primary-foreground"
                      data-testid={`forum-image-remove-${i}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {images.length < 5 && (
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground" data-testid="forum-image-picker">
                <ImagePlus className="h-4 w-4" />
                Add photo
                <input type="file" accept="image/*" multiple className="hidden" onChange={pickImages} />
              </label>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input id="tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="fanatec, dd, calibration" data-testid="forum-tags-input" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="forum-submit-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish post"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
